<template>
  <v-card elevation="2" outlined shaped tile class="my-4">
    <v-card-title>
      <div @click="expand = !expand" :style="{ cursor: 'pointer' }">Discount Packages</div>
      <div class="ml-2">
        <v-btn icon color="green" @click="moveUp(section.id)">
          <v-icon>mdi-arrow-up-bold-box</v-icon>
        </v-btn>
        <v-btn icon color="red" @click="moveDown(section.id)">
          <v-icon>mdi-arrow-down-bold-box</v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-card-subtitle v-if="expand">
      <div>ID: {{ section.id }}</div>
      <div>Type: {{ section.type }}</div>
      <div>Sort Order: {{ section.sort_order }}</div>
    </v-card-subtitle>
    <v-card-text v-if="expand">
      <div class="d-flex align-center mb-2">
        <v-spacer />
        <v-chip small color="blue lighten-4">Changes reflect in app immediately</v-chip>
      </div>

      <v-progress-linear v-if="loadingCatalogs" indeterminate color="blue" />

      <v-expansion-panels accordion>
        <v-expansion-panel v-for="catalog in catalogs" :key="catalog.id">
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <v-chip :color="catalog.status === 'active' ? 'green' : 'grey'" dark x-small class="mr-3">{{
                catalog.status
              }}</v-chip>
              <span class="font-weight-bold mr-2">{{ catalog.code }}</span>
              <span class="grey--text">{{ catalog.name && catalog.name.en }}</span>
              <v-spacer />
              <v-chip small class="mr-4">{{ (catalog.tiers || []).length }} tiers</v-chip>
              <v-btn icon color="green" @click.stop="moveCatalogUp(catalog.id)">
                <v-icon>mdi-arrow-up-bold-box</v-icon>
              </v-btn>
              <v-btn icon color="red" @click.stop="moveCatalogDown(catalog.id)">
                <v-icon>mdi-arrow-down-bold-box</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-row class="mb-2">
              <v-col>
                <div class="text-caption grey--text">TH: {{ catalog.name && catalog.name.th }}</div>
                <div class="text-caption grey--text mt-1">{{ catalog.description && catalog.description.en }}</div>
              </v-col>
              <v-col class="text-right" cols="auto">
                <AddOrEditDiscountPackage :isAdd="false" :catalog="catalog" @success="fetchCatalogs" />
              </v-col>
            </v-row>

            <!-- Tiers table -->
            <v-data-table
              :headers="tierHeaders"
              :items="catalog.tiers || []"
              hide-default-footer
              :items-per-page="-1"
              dense
              class="elevation-0 grey lighten-5 rounded"
            >
              <template v-slot:item.name="{ item }">
                {{ item.name && item.name.en }}
              </template>
              <template v-slot:item.price="{ item }"> {{ item.price | number }} ฿ </template>
              <template v-slot:item.discount_amount="{ item }">
                {{ item.discount_amount }}{{ item.discount_unit === 'percent' ? '%' : ' ฿' }}
              </template>
              <template v-slot:item.total_usage_limit="{ item }"> {{ item.total_usage_limit }} uses </template>
              <template v-slot:item.expire_after_days="{ item }"> {{ item.expire_after_days }} days </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="item.status === 'active' ? 'green' : 'grey'" dark x-small>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <AddOrEditTier :isAdd="false" :tier="item" :catalogId="catalog.id" @success="fetchCatalogs" />
              </template>
            </v-data-table>

            <div class="mt-2">
              <AddOrEditTier :isAdd="true" :catalogId="catalog.id" @success="fetchCatalogs" />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mt-4">
        <AddOrEditDiscountPackage :isAdd="true" @success="fetchCatalogs" />
      </div>
    </v-card-text>
    <v-card-actions v-if="expand">
      <v-btn text color="error" @click="deleteSection(section.id)">Remove from Home Feed</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import discountPackage from '../../../services/discountPackage';
import AddOrEditDiscountPackage from '../../DiscountPackage/components/AddOrEditDiscountPackage.vue';
import AddOrEditTier from '../../DiscountPackage/components/AddOrEditTier.vue';

export default {
  name: 'HomeFeedDiscountPackage',
  components: {
    AddOrEditDiscountPackage,
    AddOrEditTier,
  },
  props: {
    section: {
      type: Object,
      required: true,
    },
    moveUp: {
      type: Function,
      required: true,
    },
    moveDown: {
      type: Function,
      required: true,
    },
    deleteSection: {
      type: Function,
      required: true,
    },
  },
  filters: {
    number(val) {
      return Number(val).toLocaleString();
    },
  },
  data() {
    return {
      expand: false,
      loadingCatalogs: false,
      catalogs: [],
      tierHeaders: [
        { text: 'Tier', value: 'tier', sortable: false },
        { text: 'Name (EN)', value: 'name', sortable: false },
        { text: 'Price', value: 'price', sortable: true },
        { text: 'Discount', value: 'discount_amount', sortable: false },
        { text: 'Uses', value: 'total_usage_limit', sortable: false },
        { text: 'Expires', value: 'expire_after_days', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    };
  },
  watch: {
    expand(val) {
      if (val && this.catalogs.length === 0) {
        this.fetchCatalogs();
      }
    },
  },
  methods: {
    async fetchCatalogs() {
      this.loadingCatalogs = true;
      try {
        const response = await discountPackage.getAll();
        this.catalogs = response.data || [];
      } catch (error) {
        console.error('Error fetching discount package catalogs:', error);
        this.$notify({ title: 'Error', text: 'Failed to load discount packages.', type: 'error' });
      } finally {
        this.loadingCatalogs = false;
      }
    },
    async moveCatalogUp(catalogId) {
      try {
        await discountPackage.moveCatalog(catalogId, -1);
        this.fetchCatalogs();
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to move catalog up.', type: 'error' });
      }
    },
    async moveCatalogDown(catalogId) {
      try {
        await discountPackage.moveCatalog(catalogId, 1);
        this.fetchCatalogs();
      } catch (error) {
        this.$notify({ title: 'Error', text: 'Failed to move catalog down.', type: 'error' });
      }
    },
  },
};
</script>

<style scoped></style>
